/**
 * CameraCapture Component
 * Reusable camera component with preview
 */

import { useCallback } from 'react'
import Webcam from 'react-webcam'
import { Camera, RotateCcw, Check } from 'lucide-react'
import { Button } from '../ui/Button'
import { useCamera, videoConstraints } from '../../hooks/useCamera'
import { cn } from '../../utils/helpers'
import { compressImage } from '../../utils/imageUtils'

interface CameraCaptureProps {
  onCapture: (photoData: string) => void
  onClose?: () => void
  onError?: (error: string) => void
  facingMode?: 'user' | 'environment'
  className?: string
  compress?: boolean
}

export function CameraCapture({ 
  onCapture, 
  onClose, 
  onError, 
  facingMode = 'environment',
  className,
  compress = true
}: CameraCaptureProps) {
  const {
    photoData,
    isCapturing,
    error,
    isCameraReady,
    webcamRef,
    capturePhoto,
    retakePhoto,
    setCameraReady,
  } = useCamera()

  const customConstraints = {
    ...videoConstraints,
    facingMode: { ideal: facingMode },
  }

  const handleCapture = useCallback(async () => {
    const photo = capturePhoto()
    if (photo) {
      try {
        // Compress if enabled
        const finalPhoto = compress ? await compressImage(photo) : photo
        onCapture(finalPhoto)
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to process image'
        if (onError) {
          onError(errorMsg)
        }
      }
    } else if (onError) {
      onError('Failed to capture photo')
    }
  }, [capturePhoto, onCapture, onError, compress])

  const handleRetake = useCallback(() => {
    retakePhoto()
  }, [retakePhoto])

  const handleUserMediaError = useCallback(() => {
    const errorMessage = 'Camera access denied. Please enable camera permissions.'
    if (onError) {
      onError(errorMessage)
    }
    // Also call onClose if camera fails
    if (onClose) {
      onClose()
    }
  }, [onError, onClose])

  return (
    <div className={cn('w-full', className)}>
      <div className="relative aspect-square w-full max-w-sm mx-auto overflow-hidden rounded-md bg-gray-900">
        {/* Close button */}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-2 z-10 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
          >
            <span className="sr-only">Close</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        
        {photoData ? (
          // Show captured photo
          <img
            src={photoData}
            alt="Captured"
            className="w-full h-full object-cover"
          />
        ) : (
          // Show camera feed
          <>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={customConstraints}
              onUserMedia={() => setCameraReady(true)}
              onUserMediaError={handleUserMediaError}
              className="w-full h-full object-cover"
            />
            {!isCameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-center">
                  <Camera className="h-12 w-12 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400 text-paragraph-small">
                    Initializing camera...
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-2 text-center text-paragraph-small text-negative">
          {error}
        </p>
      )}

      {/* Controls */}
      <div className="mt-4 flex justify-center gap-3">
        {photoData ? (
          <>
            <Button
              variant="outline"
              onClick={handleRetake}
              leftIcon={<RotateCcw className="h-4 w-4" />}
            >
              Retake
            </Button>
            <Button
              variant="primary"
              leftIcon={<Check className="h-4 w-4" />}
              onClick={() => onCapture(photoData)}
            >
              Use Photo
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            size="lg"
            onClick={handleCapture}
            disabled={!isCameraReady || isCapturing}
            isLoading={isCapturing}
            leftIcon={<Camera className="h-5 w-5" />}
          >
            Take Photo
          </Button>
        )}
      </div>
    </div>
  )
}
